import { SEGMENT_TYPE_MAP, SegmentFilterType } from '@/constants/reports';
import {
  PartnerPopulation,
  Population,
  SegmentType,
} from '@/types/populations';
import {
  Report,
  ReportOurSegmentFilters,
  ReportOurSegmentFiltersItem,
  ReportPartnerSegmentFilters,
  ReportPartnerSegmentFiltersItem,
  ReportSegmentFilters,
} from '@/types/reports';

/**
 * Remove empty arrays from segment_filters object.
 */
export function cleanSegmentFilters(
  segments: ReportSegmentFilters | null | undefined,
): ReportSegmentFilters {
  if (!segments) return {};

  return Object.fromEntries(
    Object.entries(segments).filter(
      ([_, vals]) => Array.isArray(vals) && vals.length > 0,
    ),
  );
}

/**
 * Extract segment filters matching a specific segment type.
 */
export function filterSegmentsByType(
  filters: ReportSegmentFilters | null | undefined,
  segmentType: SegmentType,
): ReportSegmentFilters {
  if (!filters) return {};

  const result: ReportSegmentFilters = {};

  Object.entries(filters).forEach(([orgUuid, orgFilters]) => {
    const matching = (orgFilters || []).filter(
      (f) => f.segment === segmentType,
    );
    if (matching.length > 0) {
      result[orgUuid] = matching;
    }
  });

  return result;
}

/**
 * Remove all filters of a specific segment type from all partners.
 */
export function removeSegmentType(
  filters: ReportSegmentFilters | null | undefined,
  segmentFilterType: SegmentFilterType,
): ReportSegmentFilters {
  if (!filters) return {};

  const segmentType = SEGMENT_TYPE_MAP[segmentFilterType];
  const result: ReportSegmentFilters = {};

  for (const [partnerUuid, partnerFilters] of Object.entries(filters)) {
    const remaining = partnerFilters.filter((f) => f.segment !== segmentType);
    if (remaining.length > 0) {
      result[partnerUuid] = remaining;
    }
  }

  return result;
}

/**
 * Returns the filters object if it has entries, otherwise undefined.
 * Useful for conditional assignment to report.segment_filters.
 */
export function normalizeSegmentFilters(
  filters: ReportSegmentFilters | null | undefined,
): ReportSegmentFilters | undefined {
  if (!filters) return undefined;
  return Object.keys(filters).length > 0 ? filters : undefined;
}

type ExpandedSegmentFilterOverrides = Pick<
  Partial<Report>,
  | 'our_segment_filters'
  | 'our_population_ids'
  | 'segments'
  | 'partner_segment_filters'
  | 'partner_population_ids'
  | 'partner_segments'
>;

export function convertOurSegmentsToFilters(
  segments: SegmentType[],
): ReportOurSegmentFilters {
  return { operator: 'OR', items: segments.map((segment) => ({ segment })) };
}

export function convertOurPopulationIdsToFilters(
  populationIds: number[],
  getPopulationById: (id: number) => Population | undefined,
): ReportOurSegmentFilters {
  const grouped = new Map<SegmentType, Set<number>>();
  for (const popId of populationIds) {
    const pop = getPopulationById(popId);
    if (!pop) continue;
    const segment = pop.segment as SegmentType;
    const existing = grouped.get(segment);
    if (existing) existing.add(pop.id);
    else grouped.set(segment, new Set([pop.id]));
  }
  const items: ReportOurSegmentFilters['items'] = [];
  for (const [segment, ids] of grouped) {
    const item: ReportOurSegmentFiltersItem = {
      segment,
      population_ids: Array.from(ids),
    };
    items.push(item);
  }
  return { operator: 'OR', items };
}

export function convertPartnerSegmentsToFilters(
  segments: SegmentType[],
  orgIds: number[],
  getPartnerPopulationsBySegment: (segment: SegmentType) => PartnerPopulation[],
): ReportPartnerSegmentFilters {
  const items: ReportPartnerSegmentFilters['items'] = [];
  for (const segment of segments) {
    const pops = getPartnerPopulationsBySegment(segment);
    for (const orgId of orgIds) {
      if (!pops.some((p) => p.organization_id === orgId)) continue;
      items.push({
        segment,
        organization_id: orgId,
      } as ReportPartnerSegmentFiltersItem);
    }
  }
  return { operator: 'OR', items };
}

export function convertPartnerPopulationIdsToFilters(
  populationIds: number[],
  getPartnerPopulationById: (id: number) => PartnerPopulation | undefined,
): ReportPartnerSegmentFilters {
  const grouped = new Map<
    string,
    {
      segment: SegmentType;
      organization_id: number;
      population_ids: Set<number>;
    }
  >();
  for (const popId of populationIds) {
    const pop = getPartnerPopulationById(popId);
    if (!pop) continue;
    const key = `${pop.segment}-${pop.organization_id}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.population_ids.add(pop.id);
    } else {
      grouped.set(key, {
        segment: pop.segment as SegmentType,
        organization_id: pop.organization_id,
        population_ids: new Set([pop.id]),
      });
    }
  }
  const items: ReportPartnerSegmentFilters['items'] = [];
  for (const item of grouped.values()) {
    const filterItem: ReportPartnerSegmentFiltersItem = {
      ...item,
      population_ids: Array.from(item.population_ids),
    };
    items.push(filterItem);
  }
  return { operator: 'OR', items };
}

type ConvertLegacyPopulationsLookups = {
  getPopulationById: (id: number) => Population | undefined;
  getPopulationsBySegment: (segment: SegmentType) => Population[];
  getPartnerPopulationById: (id: number) => PartnerPopulation | undefined;
  getPartnerPopulationsBySegment: (segment: SegmentType) => PartnerPopulation[];
};

export function convertLegacyPopulationsToSegmentFilters(
  report: Partial<Report>,
  lookups: ConvertLegacyPopulationsLookups,
): void {
  if (!report.our_segment_filters?.items?.length) {
    if (report.segments?.length) {
      report.our_segment_filters = convertOurSegmentsToFilters(report.segments);
    } else if (report.our_population_ids?.length) {
      report.our_segment_filters = convertOurPopulationIdsToFilters(
        report.our_population_ids,
        lookups.getPopulationById,
      );
    }
  }
  if (report.our_segment_filters?.items?.length) {
    report.segments = [];
    report.our_population_ids = [];
  }

  if (!report.partner_segment_filters?.items?.length) {
    if (report.partner_segments?.length) {
      if (!report.partner_organization_ids?.length) {
        report.partner_segment_filters = {
          items: report.partner_segments.map((segment) => ({ segment })),
          operator: 'OR',
        } as Report['partner_segment_filters'];
      } else {
        report.partner_segment_filters = convertPartnerSegmentsToFilters(
          report.partner_segments,
          report.partner_organization_ids,
          lookups.getPartnerPopulationsBySegment,
        ) as Report['partner_segment_filters'];
      }
    } else if (report.partner_population_ids?.length) {
      report.partner_segment_filters = convertPartnerPopulationIdsToFilters(
        report.partner_population_ids,
        lookups.getPartnerPopulationById,
      ) as Report['partner_segment_filters'];
    }
  }
  if (report.partner_segment_filters?.items?.length) {
    report.partner_segments = [];
    report.partner_population_ids = [];
  }
}

export function getExpandedSegmentFilterOverrides(
  report: Pick<
    Partial<Report>,
    'our_segment_filters' | 'partner_segment_filters'
  >,
): ExpandedSegmentFilterOverrides {
  const overrides: ExpandedSegmentFilterOverrides = {
    segments: [],
    partner_segments: [],
  };

  if (report.our_segment_filters?.items?.length) {
    overrides.our_segment_filters = {
      ...report.our_segment_filters,
      operator: report.our_segment_filters.operator || 'OR',
    };
    overrides.our_population_ids = [];
  }
  if (report.partner_segment_filters?.items?.length) {
    overrides.partner_segment_filters = {
      ...report.partner_segment_filters,
    };
    overrides.partner_population_ids = [];
  }

  return overrides;
}
